import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { omit } from 'rambda';
import {
    IconBuildShape,
    IconCaretRight,
    IconError,
    IconFolder,
    IconFolderMask,
    IconImage,
    IconImageMask,
    IconMask,
    IconMaskAlt3,
    IconTextField,
    IconTextMask,
    IconVideo,
    IconSound,
    IconVideoMask,
    IconVolumeOff,
    IconHideSmall,
} from '@bynder/icons';
import { Thumbnail, token, Tooltip } from '@bynder/design-system';
import { isCtrlKey } from '~/helpers/hotKeys';
import generateTestId from '~/helpers/testIdHelpers';
import { getElementType, isUsedAsMask } from '~/common/editor/helpers/elementtree';
import { isElementLocked } from '~/common/editor/timeline/timeline-actions/components/utils';
import ElementName from './elementName/ElementName';
import { useResizing } from '../dragging/useResizing';
import { useDragging } from '../dragging/useDragging';
import { elementHasAnimations } from '../track-renderer';
import useEditor from '../../../../../hooks/useEditor';
import useElementValidation from '../../../../../hooks/useElementValidation';
import useForceUpdate from '../../../../../hooks/useForceUpdate';
import ElementTimelineTransitions from './timelineTransitions/ElementTimelineTransitions';
import { TimelineElementStyled, WarningIconWrapper } from './Element.styled';
import useObjectUrl from 'packages/hooks/useObjectUrl';
import { AudioElement } from '@bynder-studio/render-core';

const checkIsHiddenByParent = (element) =>
    element.parent && !element.hidden ? checkIsHiddenByParent(element.parent) : element.hidden;

const useBind = (callBack, ...boundedArgs) =>
    useCallback(
        (...args) => {
            if (typeof callBack === 'function') {
                callBack(...boundedArgs, ...args);
            }
        },
        [callBack, boundedArgs],
    );

export const Element = ({
    item,
    onClick,
    onExpandClick,
    onResize,
    onResizeEnd,
    onDrag,
    onDrop,
    onCancelDragging,
    onAnimationExpand,
    containerRef,
    tracksRef,
    trackDuration,
    scrollContainerRef,
}) => {
    const elementNameNodeRef = useRef(null);
    const elementRef = useRef<HTMLDivElement>();
    const { element, style, expanded, animationsExpanded } = item;
    const { name: elementName, id, parent, contentPropertyId } = element;
    const { creativeModel, manipulationRenderer } = useEditor();
    const { isValid, isChildrenValid, validationMessages } = useElementValidation(id);
    const forceUpdate = useForceUpdate();
    const [selected, setSelected] = useState(false);
    const isGroup = !!item.children;
    const isParentHidden = checkIsHiddenByParent(element);
    const urlObject = useObjectUrl(element.src);
    const validationMessage = useMemo(() => Object.values(validationMessages).join(',\n'), [validationMessages]);

    const getElementTypeForTestId = (el) => {
        const isMask = isUsedAsMask(creativeModel, el);
        const isElementMasked = el?.mask !== null;
        const type = getElementType(el);
        const postfix = isMask ? '_mask' : isElementMasked ? '_masked' : '';

        return `${type}${postfix}`;
    };

    const mapElementIcon = {
        mask: <IconMask />,
        image: urlObject ? (
            // @ts-ignore
            <Thumbnail imageUrl={urlObject} style={{ width: '24px', height: '24px', objectFit: 'cover' }} />
        ) : (
            <IconImage />
        ),
        'image-masked': <IconImageMask />,
        video: <IconVideo />,
        'video-masked': <IconVideoMask />,
        audio: <IconSound />,
        text: <IconTextField />,
        'text-masked': <IconTextMask />,
        shape: <IconBuildShape />,
        'shape-masked': <IconMaskAlt3 />,
        group: <IconFolder />,
        'group-masked': <IconFolderMask />,
    };

    const wrapIconTooltipIfNeeded = (icon) => {
        if (!validationMessage) {
            return icon;
        }

        return (
            <Tooltip content={validationMessage} position="top-right">
                {icon}
            </Tooltip>
        );
    };

    const getElementIcon = (el) => {
        if (!isValid || !isChildrenValid) {
            return (
                <WarningIconWrapper>
                    <IconError style={{ color: token.red400 }} />
                </WarningIconWrapper>
            );
        }

        const isMask = isUsedAsMask(creativeModel, el);

        if (isMask) {
            return mapElementIcon['mask'];
        }

        const isElementMasked = 'mask' in el && !!el.mask;

        const type = getElementType(el).toLowerCase();

        if (isElementMasked) {
            return mapElementIcon[`${type}-masked`];
        }

        return mapElementIcon[type];
    };

    const type = getElementTypeForTestId(element).toLowerCase();
    const testId = useMemo(() => generateTestId(`timeline_element_${type}_${id}`), [type, id]);
    const color = contentPropertyId ? token.teal500 : token.blue500;
    const backgroundColor = contentPropertyId ? token.teal500a10 : token.blue500a10;

    const handleDrag = useBind(onDrag, item);
    const handleDrop = useBind(onDrop, item);
    const handleAnimationExpand = useBind(onAnimationExpand, item);
    const handleResize = useBind(onResize, item);
    const handleResizeEnd = useBind(onResizeEnd, item, elementRef);

    const handleNameChange = useCallback(
        (name) => {
            creativeModel.updateElement(item.element.id, { name });
        },
        [item, creativeModel],
    );

    const [onResizeMouseDown, resizeModeRef] = useResizing(
        elementRef,
        containerRef,
        handleResize,
        handleResizeEnd,
        element,
        { blockRightEdge: false },
    );

    const handleMouseUp = (evt) => {
        onClick(item, evt);
    };

    const [dragging, onDragStart] = useDragging({
        elementRef,
        containerRef,
        scrollContainerRef,
        tracksRef,
        onDragCb: handleDrag,
        onDrop: handleDrop,
        handleMouseUp,
        onCancel: onCancelDragging,
        reactToPeers: selected,
    });

    const onEdgeClick = useCallback(
        (evt) => {
            if (isGroup) {
                evt.stopPropagation();
                onExpandClick(item);
            }
        },
        [isGroup, item, onExpandClick],
    );
    const onEdgeMouseDown = useCallback(
        (e) => {
            if (isGroup) {
                e.stopPropagation();
            }
        },
        [isGroup],
    );

    const onDragEnableMouseDown = (evt: React.MouseEvent) => {
        if (!isCtrlKey(evt) && evt.button === 0 && onDragStart instanceof Function) {
            onDragStart(evt);
        }
    };

    const elementWidth = useMemo(() => {
        return (
            containerRef.current && (containerRef.current.getBoundingClientRect().width * parseFloat(style.width)) / 100
        );
    }, [containerRef.current && containerRef.current.getBoundingClientRect().width, style.width]);

    // todo: Remove after BE implements inherited locks
    const isLocked = isElementLocked(element);
    // const isLocked = element.locked;

    const onElementClick = useCallback(
        (evt: React.MouseEvent) => {
            if (!dragging.current && !resizeModeRef.current && onClick instanceof Function) {
                onClick(item, evt);
            }
        },
        [item, onClick],
    );

    const classNames = useMemo(() => {
        const temp = ['timeline__element'];

        selected && temp.push('selected');
        isGroup && temp.push('is-group');
        !isChildrenValid && temp.push('hasMinorError');
        !isValid && temp.push('hasMajorError');
        isParentHidden && temp.push('hidden');
        contentPropertyId && temp.push('content-property');

        return temp;
    }, [selected, isGroup, isChildrenValid, isValid, isParentHidden, contentPropertyId]);

    const lockedAttr = useMemo(() => (DEFINE_TESTID_ATTRIBUTES ? { 'data-locked': isLocked } : {}), [isLocked]);

    const icon = wrapIconTooltipIfNeeded(getElementIcon(element));
    const hideIcon = item.element instanceof AudioElement ? <IconVolumeOff size="16" /> : <IconHideSmall size="16" />;

    useEffect(() => {
        if (!manipulationRenderer) {
            return;
        }

        setSelected(manipulationRenderer.isElementSelected(id));
        const elementSelectedListener = () => setSelected(manipulationRenderer.isElementSelected(id));

        manipulationRenderer.eventEmitter.on('elementSelected', elementSelectedListener);
        creativeModel.on('elementUpdated', forceUpdate);

        return () => {
            manipulationRenderer.eventEmitter.off('elementSelected', elementSelectedListener);
            creativeModel.off('elementUpdated', forceUpdate);
        };
    }, [id, manipulationRenderer]);

    useEffect(() => {
        Object.entries(style).forEach(([key, value]) => {
            if (elementRef.current) {
                elementRef.current.style[key] = value;
            }
        });
    }, [style]);

    return (
        <>
            <TimelineElementStyled
                className={classNames.join(' ')}
                onClick={onElementClick}
                ref={elementRef}
                data-id={id}
                data-group-id={isGroup ? id : parent?.id}
                data-is-group={isGroup}
                data-is-expanded={expanded}
                {...testId}
                style={style}
                onMouseDown={onDragEnableMouseDown}
                {...lockedAttr}
            >
                <div className="timeline__element__left-edge" onClick={onEdgeClick} onMouseDown={onEdgeMouseDown}>
                    {isGroup ? (
                        <div className={`timeline__element__expand-icon ${expanded ? 'expanded' : ''}`}>
                            <IconCaretRight />
                        </div>
                    ) : (
                        <div
                            className="timeline__element__resize-handle resize-handle-left"
                            onMouseDown={(evt) => onResizeMouseDown(evt, 'left')}
                        />
                    )}
                </div>
                <ElementName
                    id={id}
                    name={elementName}
                    duration={element.duration}
                    onNameChange={handleNameChange}
                    icon={icon}
                    hideIcon={hideIcon}
                    isGroup={isGroup}
                    childrenLength={element.children?.length}
                    elementWidth={elementWidth}
                    onAnimationExpand={handleAnimationExpand}
                    hasAnimations={elementHasAnimations(element)}
                    isHidden={element.hidden}
                    isParentHidden={isParentHidden}
                    elementNameNodeRef={elementNameNodeRef}
                    isLocked={isLocked}
                    scrollContainerRef={scrollContainerRef}
                />
                {!isGroup && (
                    <div
                        className="timeline__element__resize-handle resize-handle-right"
                        onMouseDown={(evt) => onResizeMouseDown(evt, 'right')}
                    />
                )}
            </TimelineElementStyled>

            {animationsExpanded && (
                <ElementTimelineTransitions
                    trackContainerRef={containerRef}
                    trackDuration={trackDuration}
                    element={element}
                    color={color}
                    backgroundColor={backgroundColor}
                    elementStyle={omit('backgroundColor', style)}
                />
            )}
        </>
    );
};
